import { getImageUrl as getImageUrlFromConfig } from '../config';
import placeholderImage from '../images/Kurti1.avif';

export const getProp = (obj, camelCase) => {
  if (!obj) return undefined;
  const pascalCase = camelCase.charAt(0).toUpperCase() + camelCase.slice(1);
  return obj[camelCase] !== undefined ? obj[camelCase] : obj[pascalCase];
};

export const getImageUrl = (url) => getImageUrlFromConfig(url, placeholderImage);

export const formatPrice = (num) => {
  if (num == null || num === '' || Number.isNaN(Number(num))) return 'N/A';
  return Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const getProductMrp = (product) => {
  const price = getProp(product, 'price');
  const originalPrice = getProp(product, 'originalPrice') ?? product.originalPrice;
  const numPrice = Number(price);
  const numOriginal = originalPrice != null && originalPrice !== '' ? Number(originalPrice) : null;
  if (numOriginal != null && !Number.isNaN(numOriginal) && numOriginal > 0) return numOriginal;
  return numPrice;
};

/** True if URL points to YouTube (watch, embed, shorts, youtu.be). */
export function isYouTubeUrl(url) {
  if (url == null || String(url).trim() === '') return false;
  const s = String(url).trim().toLowerCase();
  return s.includes('youtube.com/') || s.includes('youtu.be/');
}

/** Extract YouTube video id from common URL formats. */
export function getYouTubeVideoId(url) {
  if (!url) return null;
  const s = String(url).trim();
  try {
    const u = new URL(s.startsWith('http') ? s : `https://${s}`);
    const host = u.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '').split('/')[0];
      return id || null;
    }
    if (host.includes('youtube.com')) {
      if (u.pathname.startsWith('/watch')) {
        return u.searchParams.get('v');
      }
      if (u.pathname.startsWith('/embed/') || u.pathname.startsWith('/v/')) {
        return u.pathname.split('/').filter(Boolean)[1] || null;
      }
      if (u.pathname.startsWith('/shorts/')) {
        return u.pathname.split('/').filter(Boolean)[1] || null;
      }
    }
  } catch {
    return null;
  }
  return null;
}

export function getYouTubeWatchUrl(url) {
  const id = getYouTubeVideoId(url);
  if (!id) return String(url || '').trim();
  return `https://www.youtube.com/watch?v=${id}`;
}

export function normalizeYouTubeUrl(url) {
  const trimmed = String(url || '').trim();
  if (!trimmed) return '';
  return getYouTubeWatchUrl(trimmed);
}

export function isValidYouTubeUrl(url) {
  return isYouTubeUrl(url) && Boolean(getYouTubeVideoId(url));
}

export const MEN_WOMEN_SIZE_NAMES = ['S', 'M', 'L', 'XL', 'XXL', '3XL'];
export const KIDS_SIZE_NAMES = [
  '0-1yr', '1-2yr', '2-3yr', '3-4yr', '4-5yr', '5-6yr', '6-7yr', '7-8yr',
  '8-9yr', '9-10yr', '10-11yr', '11-12yr', '12-13yr', '13-14yr', '14-15yr', '15-16yr',
];

export function normalizeLookupValue(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function isClothesOrGarmentsCategory({ categoryId, categoryName } = {}) {
  const id = Number(categoryId);
  if (id === 2) return true;
  const name = String(categoryName || '').toLowerCase();
  return /cloth|garment|apparel|fashion/.test(name);
}

export function getClothesSizeOptions(allSizes, subCategoryName) {
  const sizes = Array.isArray(allSizes) ? allSizes : [];
  const normalized = normalizeLookupValue(subCategoryName);
  const isKidsWearSubCategory = normalized.includes('kid');
  const isMenWomenSubCategory = !isKidsWearSubCategory && (
    normalized.includes('men') || normalized.includes('women')
  );
  const preferredSizeNames = isKidsWearSubCategory
    ? KIDS_SIZE_NAMES
    : isMenWomenSubCategory
      ? MEN_WOMEN_SIZE_NAMES
      : [];
  const preferredSizeMap = new Map(
    sizes.map((size) => [normalizeLookupValue(size.sizeName), size])
  );
  const preferredOptions = preferredSizeNames
    .map((sizeName) => preferredSizeMap.get(normalizeLookupValue(sizeName)))
    .filter(Boolean);
  const sizeOptions = preferredOptions.length > 0
    ? preferredOptions
    : [...sizes].sort((a, b) => String(a.sizeName).localeCompare(String(b.sizeName), undefined, { numeric: true }));
  const missingSizeNames = preferredSizeNames.filter((sizeName) => !preferredSizeMap.has(normalizeLookupValue(sizeName)));
  return {
    sizeOptions,
    preferredSizeNames,
    missingSizeNames,
    isKidsWearSubCategory,
    isMenWomenSubCategory,
  };
}

export function getListedProductVariants(product) {
  const raw = product?.variants ?? product?.Variants;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.$values)) return raw.$values;
  return [];
}

export function getListedProductStock(product) {
  const variants = getListedProductVariants(product);
  if (variants.length > 0) {
    return variants.reduce((sum, variant) => sum + Number(variant.stock ?? variant.Stock ?? 0), 0);
  }
  return Number(product?.stock ?? product?.Stock ?? 0);
}
